import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, AlertTriangle, Shield, Trash2,
  Loader2, X, Flame, Zap,
  Lightbulb, Clock, DollarSign, Edit2
} from 'lucide-react';

const RISK_TYPES = [
  { value: 'physical', label: 'Physical Risk', icon: Flame, color: 'text-red-600 bg-red-50' },
  { value: 'transition', label: 'Transition Risk', icon: Zap, color: 'text-amber-600 bg-amber-50' },
  { value: 'opportunity', label: 'Opportunity', icon: Lightbulb, color: 'text-green-600 bg-green-50', plural: 'Opportunities' }
];

const CATEGORIES = {
  physical: [
    { value: 'acute_physical', label: 'Acute Physical (extreme weather events)' },
    { value: 'chronic_physical', label: 'Chronic Physical (long-term shifts)' }
  ],
  transition: [
    { value: 'policy_legal', label: 'Policy & Legal' },
    { value: 'technology', label: 'Technology' },
    { value: 'market', label: 'Market' },
    { value: 'reputation', label: 'Reputation' }
  ],
  opportunity: [
    { value: 'resource_efficiency', label: 'Resource Efficiency' },
    { value: 'energy_source', label: 'Energy Source' },
    { value: 'products_services', label: 'Products & Services' },
    { value: 'markets', label: 'Markets' },
    { value: 'resilience', label: 'Resilience' }
  ]
};

const LIKELIHOODS = [
  { value: 'rare', label: 'Rare', score: 1 },
  { value: 'unlikely', label: 'Unlikely', score: 2 },
  { value: 'possible', label: 'Possible', score: 3 },
  { value: 'likely', label: 'Likely', score: 4 },
  { value: 'almost_certain', label: 'Almost Certain', score: 5 }
];

const IMPACTS = [
  { value: 'negligible', label: 'Negligible', score: 1 },
  { value: 'minor', label: 'Minor', score: 2 },
  { value: 'moderate', label: 'Moderate', score: 3 },
  { value: 'major', label: 'Major', score: 4 },
  { value: 'catastrophic', label: 'Catastrophic', score: 5 }
];

const TIME_HORIZONS = [
  { value: 'short', label: 'Short-term (0-1 year)' },
  { value: 'medium', label: 'Medium-term (1-5 years)' },
  { value: 'long', label: 'Long-term (5+ years)' }
];

const STATUSES = [
  { value: 'identified', label: 'Identified', color: 'bg-slate-100 text-slate-700' },
  { value: 'assessed', label: 'Assessed', color: 'bg-blue-100 text-blue-700' },
  { value: 'mitigating', label: 'Mitigating', color: 'bg-amber-100 text-amber-700' },
  { value: 'monitored', label: 'Monitored', color: 'bg-green-100 text-green-700' },
  { value: 'closed', label: 'Closed', color: 'bg-slate-200 text-slate-500' }
];

const ClimateRisksPage = () => {
  const navigate = useNavigate();
  const { currentOrg, user, userData } = useOutletContext();
  const currentUserId = userData?.id;
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [editingRisk, setEditingRisk] = useState(null);

  // Calculate risk score based on likelihood and impact
  const calculateRiskScore = (likelihood, impact) => {
    const likelihoodScore = LIKELIHOODS.find(l => l.value === likelihood)?.score || 0;
    const impactScore = IMPACTS.find(i => i.value === impact)?.score || 0;
    return likelihoodScore * impactScore;
  };


  // Fetch risks - direct Supabase query
  const fetchRisks = useCallback(async () => {
    if (!currentOrg?.id) return;

    try {
      const { data, error } = await supabase
        .from('climate_risks')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate risk scores for each risk
      const risksWithScores = (data || []).map(risk => ({
        ...risk,
        risk_score: calculateRiskScore(risk.likelihood, risk.impact)
      }));

      setRisks(risksWithScores);
    } catch (error) {
      console.error('Error fetching risks:', error);
      toast.error('Failed to load climate risks');
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.id]);

  useEffect(() => {
    if (currentOrg) {
      fetchRisks();
    }
  }, [currentOrg?.id, fetchRisks]);

  useEffect(() => {
    if (currentOrg) {
      fetchRisks();
    }
  }, [currentOrg, fetchRisks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.risk_type || !formData.category || !formData.title) {
      toast.error('Please fill in required fields');
      return;
    }
    if (!currentOrg) {
      toast.error('Organisation not loaded');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        organisation_id: currentOrg.id,
        risk_type: formData.risk_type,
        category: formData.category,
        title: formData.title,
        description: formData.description || null,
        likelihood: formData.likelihood || null,
        impact: formData.impact || null,
        time_horizon: formData.time_horizon || null,
        financial_impact_low: formData.financial_impact_low ? parseFloat(formData.financial_impact_low) : null,
        financial_impact_high: formData.financial_impact_high ? parseFloat(formData.financial_impact_high) : null,
        mitigation_strategy: formData.mitigation_strategy || null,
        status: formData.status,
        updated_at: new Date().toISOString()
      };

      if (editingRisk) {
        // Update existing risk
        const { error } = await supabase
          .from('climate_risks')
          .update(payload)
          .eq('id', editingRisk.id)
          .eq('organisation_id', currentOrg.id);

        if (error) throw error;

        // Log to audit
        await supabase.from('audit_logs').insert({
          id: crypto.randomUUID(),
          user_id: currentUserId,
          organisation_id: currentOrg.id,
          resource_type: 'climate_risk',
          resource_id: editingRisk.id,
          action: 'update',
          description: `Updated climate risk: ${formData.title}`
        });

        toast.success('Risk updated successfully');
      } else {
        // Create new risk
        const newId = crypto.randomUUID();
        const { error } = await supabase
          .from('climate_risks')
          .insert({
            id: newId,
            ...payload,
            created_by: currentUserId,
            created_at: new Date().toISOString()
          });

        if (error) throw error;

        // Log to audit
        await supabase.from('audit_logs').insert({
          id: crypto.randomUUID(),
          user_id: currentUserId,
          organisation_id: currentOrg.id,
          resource_type: 'climate_risk',
          resource_id: newId,
          action: 'create',
          description: `Added climate risk: ${formData.title}`
        });

        toast.success('Risk added successfully');
      }

      setShowForm(false);
      setEditingRisk(null);
      resetForm();
      fetchRisks();
    } catch (error) {
      console.error('Error saving risk:', error);
      toast.error('Failed to save risk');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (riskId) => {
    if (!window.confirm('Are you sure you want to delete this risk?')) return;
    if (!currentOrg) return;

    try {
      const riskToDelete = risks.find(r => r.id === riskId);

      const { error } = await supabase
        .from('climate_risks')
        .delete()
        .eq('id', riskId)
        .eq('organisation_id', currentOrg.id);

      if (error) throw error;

      // Log to audit
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        user_id: currentUserId,
        organisation_id: currentOrg.id,
        resource_type: 'climate_risk',
        resource_id: riskId,
        action: 'delete',
        description: `Deleted climate risk: ${riskToDelete?.title || 'Unknown'}`
      });

      toast.success('Risk deleted');
      fetchRisks();
    } catch (error) {
      console.error('Error deleting risk:', error);
      toast.error('Failed to delete risk');
    }
  };

  const handleEdit = (risk) => {
    setEditingRisk(risk);
    setFormData({
      risk_type: risk.risk_type,
      category: risk.category,
      title: risk.title,
      description: risk.description || '',
      likelihood: risk.likelihood || '',
      impact: risk.impact || '',
      time_horizon: risk.time_horizon || '',
      financial_impact_low: risk.financial_impact_low?.toString() || '',
      financial_impact_high: risk.financial_impact_high?.toString() || '',
      mitigation_strategy: risk.mitigation_strategy || '',
      status: risk.status
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      risk_type: '',
      category: '',
      title: '',
      description: '',
      likelihood: '',
      impact: '',
      time_horizon: '',
      financial_impact_low: '',
      financial_impact_high: '',
      mitigation_strategy: '',
      status: 'identified'
    });
  };

  const getRiskScoreColor = (score) => {
    if (!score) return 'bg-slate-100 text-slate-600';
    if (score >= 20) return 'bg-red-100 text-red-800';
    if (score >= 12) return 'bg-amber-100 text-amber-800';
    if (score >= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const filteredRisks = filterType === 'all'
    ? risks
    : risks.filter(r => r.risk_type === filterType);

  const riskCounts = {
    physical: risks.filter(r => r.risk_type === 'physical').length,
    transition: risks.filter(r => r.risk_type === 'transition').length,
    opportunity: risks.filter(r => r.risk_type === 'opportunity').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Shield className="w-7 h-7 mr-3 text-[#74A57F]" />
              Climate Risk Register
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Track and manage climate-related risks and opportunities
            </p>
          </div>
          <Button
            onClick={() => { resetForm(); setEditingRisk(null); setShowForm(true); }}
            className="bg-[#003B5C] hover:bg-[#002A42] text-white"
            data-testid="add-risk-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Risk
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {RISK_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <button
                key={type.value}
                onClick={() => setFilterType(filterType === type.value ? 'all' : type.value)}
                className={`p-4 rounded-xl border transition-all ${filterType === type.value
                  ? 'ring-2 ring-[#003B5C] border-[#003B5C]'
                  : 'border-slate-200 hover:border-slate-300'
                  } bg-white`}
                data-testid={`filter-${type.value}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm text-slate-500">{type.plural || `${type.label}s`}</p>
                      <p className="text-2xl font-bold text-[#003B5C]">{riskCounts[type.value]}</p>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 mb-6">
          <p className="text-sm text-blue-800">
            <strong>AASB S2 Requirement:</strong> Climate-related disclosures must include identified physical and
            transition risks, their potential financial impacts, and the strategies in place to manage them.
          </p>
        </div>

        {/* Risks List */}
        {filteredRisks.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-100">
            <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">No risks recorded yet</h3>
            <p className="text-slate-500 mb-4">
              Start building your climate risk register to meet AASB S2 requirements
            </p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-[#003B5C] hover:bg-[#002A42] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Risk
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRisks.map(risk => {
              const typeInfo = RISK_TYPES.find(t => t.value === risk.risk_type);
              const TypeIcon = typeInfo?.icon || AlertTriangle;
              const statusInfo = STATUSES.find(s => s.value === risk.status);

              return (
                <div
                  key={risk.id}
                  className="bg-white rounded-xl p-5 border border-slate-100 hover:shadow-md transition-shadow"
                  data-testid={`risk-card-${risk.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${typeInfo?.color}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-[#003B5C]">{risk.title}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusInfo?.color}`}>
                            {statusInfo?.label}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mb-2">
                          {CATEGORIES[risk.risk_type]?.find(c => c.value === risk.category)?.label || risk.category}
                        </p>
                        {risk.description && (
                          <p className="text-sm text-slate-600 mb-3">{risk.description}</p>
                        )}
                        <div className="flex flex-wrap gap-3 text-xs">
                          {risk.risk_score > 0 && (
                            <span className={`px-2 py-1 rounded-full font-medium ${getRiskScoreColor(risk.risk_score)}`}>
                              Risk Score: {risk.risk_score}/25
                            </span>
                          )}
                          {risk.time_horizon && (
                            <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {TIME_HORIZONS.find(t => t.value === risk.time_horizon)?.label}
                            </span>
                          )}
                          {(risk.financial_impact_low || risk.financial_impact_high) && (
                            <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                              <DollarSign className="w-3 h-3 inline mr-1" />
                              {risk.financial_impact_low && risk.financial_impact_high
                                ? `$${(risk.financial_impact_low / 1000).toFixed(0)}K - $${(risk.financial_impact_high / 1000).toFixed(0)}K`
                                : risk.financial_impact_high
                                  ? `Up to $${(risk.financial_impact_high / 1000).toFixed(0)}K`
                                  : `From $${(risk.financial_impact_low / 1000).toFixed(0)}K`
                              }
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(risk)}
                        className="text-slate-500 hover:text-[#003B5C]"
                        data-testid={`edit-risk-${risk.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(risk.id)}
                        className="text-slate-400 hover:text-red-600"
                        data-testid={`delete-risk-${risk.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {editingRisk ? 'Edit Risk' : 'Add Climate Risk'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingRisk(null); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Risk Type */}
              <div>
                <Label className="text-slate-700 mb-2 block">Risk Type *</Label>
                <div className="grid grid-cols-3 gap-3">
                  {RISK_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, risk_type: type.value, category: '' })}
                        className={`p-3 rounded-lg border-2 transition-all ${formData.risk_type === type.value
                          ? 'border-[#003B5C] bg-[#003B5C]/5'
                          : 'border-slate-200 hover:border-slate-300'
                          }`}
                        data-testid={`risk-type-${type.value}`}
                      >
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${formData.risk_type === type.value ? 'text-[#003B5C]' : 'text-slate-400'
                          }`} />
                        <span className={`text-sm font-medium ${formData.risk_type === type.value ? 'text-[#003B5C]' : 'text-slate-600'
                          }`}>{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category */}
              {formData.risk_type && (
                <div>
                  <Label className="text-slate-700">Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="mt-1" data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES[formData.risk_type]?.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Title */}
              <div>
                <Label className="text-slate-700">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Increased flooding risk to warehouse facilities"
                  className="mt-1"
                  data-testid="risk-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <Label className="text-slate-700">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of the risk or opportunity..."
                  className="mt-1"
                  rows={3}
                  data-testid="risk-description-input"
                />
              </div>

              {/* Likelihood & Impact */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">Likelihood</Label>
                  <Select value={formData.likelihood} onValueChange={(v) => setFormData({ ...formData, likelihood: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select likelihood" />
                    </SelectTrigger>
                    <SelectContent>
                      {LIKELIHOODS.map(l => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700">Impact</Label>
                  <Select value={formData.impact} onValueChange={(v) => setFormData({ ...formData, impact: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select impact" />
                    </SelectTrigger>
                    <SelectContent>
                      {IMPACTS.map(i => (
                        <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Time Horizon & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">Time Horizon</Label>
                  <Select value={formData.time_horizon} onValueChange={(v) => setFormData({ ...formData, time_horizon: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select time horizon" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_HORIZONS.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Financial Impact */}
              <div>
                <Label className="text-slate-700">Estimated Financial Impact (AUD)</Label>
                <div className="grid grid-cols-2 gap-4 mt-1">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      value={formData.financial_impact_low}
                      onChange={(e) => setFormData({ ...formData, financial_impact_low: e.target.value })}
                      placeholder="Low estimate"
                      className="pl-7"
                    />
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      value={formData.financial_impact_high}
                      onChange={(e) => setFormData({ ...formData, financial_impact_high: e.target.value })}
                      placeholder="High estimate"
                      className="pl-7"
                    />
                  </div>
                </div>
              </div>

              {/* Mitigation Strategy */}
              <div>
                <Label className="text-slate-700">Mitigation Strategy / Response</Label>
                <Textarea
                  value={formData.mitigation_strategy}
                  onChange={(e) => setFormData({ ...formData, mitigation_strategy: e.target.value })}
                  placeholder="Describe how this risk will be managed or how this opportunity will be captured..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </form>

            <div className="p-6 border-t border-slate-100 flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowForm(false); setEditingRisk(null); }}
                className="flex-1"
                data-testid="cancel-risk-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-[#003B5C] hover:bg-[#002A42] text-white"
                data-testid="submit-risk-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editingRisk ? 'Update Risk' : 'Add Risk'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClimateRisksPage;
